import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Copy, Code2, ExternalLink } from "lucide-react";

/**
 * BusinessEmbedWidgetCard — dashboard widget generator.
 *
 * Renders a live iframe preview of the business's public photo-slider embed
 * plus a configurable snippet the owner can paste onto their own site. The
 * snippet is a plain <iframe> — simpler and safer than a hosted JS loader,
 * and works inside every CMS block (WordPress, Squarespace, Webflow, Shopify).
 *
 * Configurable: width, height, theme, corners, CTA visibility. Every change
 * updates both the iframe preview and the copyable code in real time.
 */
export default function BusinessEmbedWidgetCard({ businessId }) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const [width, setWidth] = useState(360);
  const [height, setHeight] = useState(440);
  const [theme, setTheme] = useState("light");
  const [rounded, setRounded] = useState(true);
  const [showCta, setShowCta] = useState(true);

  const src = useMemo(() => {
    const qs = new URLSearchParams();
    if (theme === "dark") qs.set("theme", "dark");
    if (!rounded) qs.set("rounded", "0");
    if (!showCta) qs.set("showcta", "0");
    const s = qs.toString();
    return `${origin}/embed/business/${businessId}${s ? `?${s}` : ""}`;
  }, [origin, businessId, theme, rounded, showCta]);

  const snippet = useMemo(() => {
    const w = clamp(parseInt(width || 360, 10), 200, 1200);
    const h = clamp(parseInt(height || 440, 10), 200, 1200);
    return [
      `<!-- Servrevu photo widget — ${businessId} -->`,
      `<iframe`,
      `  src="${src}"`,
      `  width="${w}"`,
      `  height="${h}"`,
      `  frameborder="0"`,
      `  scrolling="no"`,
      `  loading="lazy"`,
      `  style="border:0;overflow:hidden;max-width:100%;display:block"`,
      `  title="Servrevu photo slider"`,
      `></iframe>`,
    ].join("\n");
  }, [src, width, height, businessId]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      toast.success("Embed code copied to clipboard");
    } catch {
      toast.error("Copy failed — please select the code manually.");
    }
  };

  return (
    <Card className="rounded-md p-6" data-testid="embed-widget-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Code2 size={16} className="text-[#B31E1E]" />
            <h3 className="font-display text-xl">Website widget</h3>
          </div>
          <p className="mt-1 text-sm text-gray-600 max-w-2xl">
            Drop your Servrevu photo slider on your own website. It auto-rotates through your gallery and customer photos and links visitors back to your profile with reviews and offers.
          </p>
        </div>
        <a
          href={src}
          target="_blank"
          rel="noreferrer"
          className="text-xs font-mono text-gray-500 hover:text-[#B31E1E] inline-flex items-center gap-1"
          data-testid="embed-open-preview"
        >
          Open preview <ExternalLink size={11} />
        </a>
      </div>

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-[minmax(240px,320px)_1fr] gap-6 items-start">
        {/* Live preview */}
        <div className="flex flex-col items-center">
          <div
            className="rounded-md bg-gray-50 border border-border p-3 flex items-center justify-center"
            style={{ minHeight: 200 }}
          >
            <iframe
              key={src}
              src={src}
              width={clamp(parseInt(width || 360, 10), 200, 1200)}
              height={clamp(parseInt(height || 440, 10), 200, 1200)}
              frameBorder="0"
              scrolling="no"
              loading="lazy"
              style={{ border: 0, overflow: "hidden", maxWidth: "100%", display: "block" }}
              title="Servrevu widget preview"
              data-testid="embed-widget-preview-iframe"
            />
          </div>
          <p className="mt-2 text-[11px] font-mono text-gray-500">Live preview · {width}×{height}</p>
        </div>

        {/* Controls + snippet */}
        <div className="min-w-0 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="embed-width" className="text-xs text-gray-600">Width (px)</Label>
              <Input
                id="embed-width"
                type="number"
                min={200}
                max={1200}
                value={width}
                onChange={(e) => setWidth(e.target.value)}
                data-testid="embed-width-input"
                className="rounded-sm mt-1"
              />
            </div>
            <div>
              <Label htmlFor="embed-height" className="text-xs text-gray-600">Height (px)</Label>
              <Input
                id="embed-height"
                type="number"
                min={200}
                max={1200}
                value={height}
                onChange={(e) => setHeight(e.target.value)}
                data-testid="embed-height-input"
                className="rounded-sm mt-1"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs text-gray-600">Theme</Label>
            <RadioGroup value={theme} onValueChange={setTheme} className="mt-1 flex gap-4" data-testid="embed-theme-group">
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <RadioGroupItem value="light" id="embed-theme-light" data-testid="embed-theme-light" /> Light
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <RadioGroupItem value="dark" id="embed-theme-dark" data-testid="embed-theme-dark" /> Dark
              </label>
            </RadioGroup>
          </div>

          <div className="flex flex-wrap gap-4 text-sm">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={rounded}
                onChange={(e) => setRounded(e.target.checked)}
                className="rounded"
                data-testid="embed-rounded-toggle"
              />
              Rounded corners
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showCta}
                onChange={(e) => setShowCta(e.target.checked)}
                className="rounded"
                data-testid="embed-showcta-toggle"
              />
              Show &quot;View on Servrevu&quot; pill
            </label>
          </div>

          <div>
            <div className="flex items-center justify-between gap-2 mb-2">
              <Label className="text-xs text-gray-600">Copy &amp; paste this HTML into your website</Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={copy}
                data-testid="embed-copy-button"
                className="rounded-sm h-8"
              >
                <Copy size={13} className="mr-1.5" /> Copy code
              </Button>
            </div>
            <pre
              className="rounded-md bg-gray-900 text-gray-100 text-xs p-3 overflow-x-auto font-mono leading-relaxed"
              data-testid="embed-snippet"
            >
{snippet}
            </pre>
            <p className="mt-2 text-[11px] text-gray-500">
              Works with WordPress custom-HTML blocks, Squarespace code blocks, Webflow embeds, Shopify sections and any static HTML.
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
}

function clamp(n, min, max) {
  if (Number.isNaN(n)) return min;
  return Math.max(min, Math.min(max, n));
}
